import { useCallback, useSyncExternalStore } from 'react';

const modelSnapshots = new WeakMap();

const emptyState = {};
const emptySnapshot = {
  version: 0,
  state: emptyState,
  listState: emptyState,
  requests: emptyState,
  invalid: emptyState,
  user: null
};

const getModelVersion = (model) => {
  return model?._version || 0;
};

const getModelSnapshot = (model) => {
  if (!model) return emptySnapshot;

  const version = getModelVersion(model);
  const cached = modelSnapshots.get(model);
  if (cached && cached.version === version) return cached;

  const snapshot = {
    version,
    state: typeof model.getState === 'function' ? model.getState() : model.state || emptyState,
    listState: model.listState || emptyState,
    requests: model.requests || emptyState,
    invalid: model.invalid || emptyState,
    user: typeof model.getUser === 'function' ? model.getUser() : model.user || null
  };

  modelSnapshots.set(model, snapshot);
  return snapshot;
};

export const useModel = (model, options = {}) => {
  const { base = false, reset = false, resetOnUnmount = false } = options;

  const subscribe = useCallback(
    (onStoreChange) => {
      if (!model) return () => {};

      const handleChange = () => onStoreChange();
      const subscribeMethod = base && model.baseSubscribe ? model.baseSubscribe : model.subscribe;

      subscribeMethod.call(model, handleChange, reset);

      return () => {
        model.unsubscribe(handleChange, resetOnUnmount);
      };
    },
    [base, model, reset, resetOnUnmount]
  );

  const getSnapshot = useCallback(() => getModelSnapshot(model), [model]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export default useModel;
